import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Save, Star, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getAllCausali,
  createCausale,
  updateCausale,
  deleteCausale,
} from "@/services/localDatabase";

const Causali = () => {
  const [causali, setCausali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ testo: "", predefinito: false });

  useEffect(() => {
    fetchCausali();
  }, []);

  const fetchCausali = async () => {
    try {
      setLoading(true);
      const data = await getAllCausali();
      setCausali(data);
    } catch (error) {
      console.error("Error fetching causali:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setFormData({ testo: "", predefinito: false });
    setShowDialog(true);
  };

  const openEdit = (causale) => {
    setEditingId(causale.id);
    setFormData({ testo: causale.testo || "", predefinito: !!causale.predefinito });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.testo.trim()) {
      toast.error("Il testo della causale è obbligatorio");
      return;
    }
    try {
      if (editingId) {
        await updateCausale(editingId, formData);
        toast.success("Causale aggiornata");
      } else {
        await createCausale(formData);
        toast.success("Causale creata");
      }
      setShowDialog(false);
      setFormData({ testo: "", predefinito: false });
      setEditingId(null);
      fetchCausali();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSetDefault = async (causale) => {
    try {
      await updateCausale(causale.id, { predefinito: true });
      toast.success("Causale impostata come predefinita");
      fetchCausali();
    } catch (error) {
      console.error("Error setting default:", error);
      toast.error("Errore nell'impostazione");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCausale(deleteId);
      toast.success("Causale eliminata");
      fetchCausali();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="causali-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Archivio Causali
          </h1>
          <p className="text-slate-500 mt-1">Causali di trasporto per i documenti DDT</p>
        </div>
        <Button
          onClick={openNew}
          className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-11 px-8"
          data-testid="new-causale-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuova Causale
        </Button>
      </div>

      <Card className="border-slate-100" data-testid="causali-list">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            Causali ({causali.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : causali.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessuna causale salvata</p>
              <Button onClick={openNew} className="mt-4 bg-orange-500 hover:bg-orange-600">
                Aggiungi la prima causale
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Testo Causale</th>
                    <th>Predefinita</th>
                    <th className="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {causali.map((c) => (
                    <tr key={c.id} data-testid={`causale-row-${c.id}`}>
                      <td className="font-medium">{c.testo}</td>
                      <td>
                        {c.predefinito ? (
                          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                            <Star className="w-3 h-3 mr-1 fill-orange-500 text-orange-500" />
                            Predefinita
                          </Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(c)}
                            className="text-slate-500 hover:text-orange-500"
                            data-testid={`set-default-${c.id}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Imposta come predefinita
                          </Button>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <Button
                            data-testid={`edit-causale-${c.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(c)}
                            className="text-slate-600 hover:text-blue-500"
                            title="Modifica"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-causale-${c.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(c.id)}
                            className="text-slate-600 hover:text-red-500"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">
              {editingId ? "Modifica Causale" : "Nuova Causale"}
            </DialogTitle>
            <DialogDescription>
              {editingId ? "Aggiorna la causale di trasporto" : "Aggiungi una nuova causale di trasporto"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Testo Causale *</Label>
              <Input
                value={formData.testo}
                onChange={(e) => setFormData((prev) => ({ ...prev, testo: e.target.value.toUpperCase() }))}
                placeholder="es. RESTITUZIONE C/ALLEVAMENTO"
                className="mt-1"
                data-testid="causale-testo"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="causale-predefinito"
                checked={formData.predefinito}
                onChange={(e) => setFormData((prev) => ({ ...prev, predefinito: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                data-testid="causale-predefinito"
              />
              <Label htmlFor="causale-predefinito" className="font-medium cursor-pointer">
                Imposta come predefinita
              </Label>
            </div>
            <p className="text-xs text-slate-500">
              La causale predefinita sarà preselezionata nel form Nuovo DDR. Solo una causale può essere predefinita.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleSave}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-causale-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>Sei sicuro di voler eliminare questa causale?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Causali;
